import React from 'react';
import PropTypes from 'prop-types';
const map = require('lodash/map');
const filter = require('lodash/filter');
const includes = require('lodash/includes');
const has = require('lodash/has');
const uuid = require('uuid');
import { Card, Button } from 'react-bootstrap';

const Globals = require('../../../../app/globals');
const StringUtil = require('../../../../app/shared/StringUtil');
const GlobalUserActions = require('../../actions/GlobalUserActions');
const AdminGlobalUserStore = require('../../stores/AdminGlobalUserStore');
import DateString from '../common/DateString';
import ErrorBox from '../common/ErrorBox';
import AccessibleIcon from '../common/AccessibleIcon';
import AdminUserBanEditor from './AdminUserBanEditor';
import AdminUserPartnerKeyEditor from './AdminUserPartnerKeyEditor';
import AdminLTISettingsView from './AdminLTISettingsView';
import AdminUserTemplatePermissionsEditor from './AdminUserTemplatePermissionsEditor';
import AdminUserPlanSelector from './AdminUserPlanSelector';
import AdminUserAssignmentInfo from './AdminUserAssignmentInfo';
import AdminEmulateThirdPartyUser from './AdminEmulateThirdPartyUser';
import CourseStatsView from '../analytics/CourseStatsView';
import AdminUserLocalRoleEditor from './AdminUserLocalRoleEditor';

const propTypes = {
    user: PropTypes.object.isRequired,
    channelRoles: PropTypes.object,
    onDeleteUser: PropTypes.func.isRequired,
};

const defaultProps = {
    channelRoles: null,
};

class AdminUserInfoBlock extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            userStoreState: AdminGlobalUserStore.getState(),
            actionID: null,
            resendVerActionId: null,
            verificationError: null,
            verificationSuccess: false,
            updateAccountTypeActionId: null,
            updateAccountTypeError: null,
        };

        this.getDataForUserId = this.getDataForUserId.bind(this);
        this.toggleCourseStats = this.toggleCourseStats.bind(this);
        this.resendVerification = this.resendVerification.bind(this);
        this.updateAccountType = this.updateAccountType.bind(this);
        this.unsubscribes = [
            AdminGlobalUserStore.listen(this.onStoreChange.bind(this, 'userStoreState'))
        ];
    }

    componentDidMount() {
        this.getDataForUserId(this.props.user.id);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.user.id !== this.props.user.id) {
            this.setState({
                resendVerActionId: null,
                verificationError: null,
                verificationSuccess: false,
                updateAccountTypeActionId: null,
                updateAccountTypeError: null,
            }, () => {
                this.getDataForUserId(this.props.user.id);
            });
        }
    }

    componentWillUnmount() {
        this.unsubscribes.forEach(u => u());
    }

    onStoreChange(storeStateName, state) {
        let additionalState = {};
        if (state.action.id === this.state.resendVerActionId) {
            if (state.action.error) {
                additionalState = {
                    ...additionalState,
                    resendVerActionId: null,
                    verificationSuccess: false,
                    verificationError: state.action.error,
                };
            }
            else {
                additionalState = {
                    ...additionalState,
                    resendVerActionId: null,
                    verificationSuccess: true,
                    verificationError: null,
                };
            }
        }
        else if (state.action.id === this.state.updateAccountTypeActionId) {
            if (state.action.error) {
                additionalState = {
                    ...additionalState,
                    updateAccountTypeActionId: null,
                    updateAccountTypeError: state.action.error,
                };
            }
            else {
                additionalState = {
                    ...additionalState,
                    updateAccountTypeActionId: null,
                    updateAccountTypeError: null,
                };
            }
        }

        this.setState({ [storeStateName]: state, ...additionalState });
    }

    getDataForUserId(userId) {
        const launchCount = this.state.userStoreState.launchCounts[userId];
        if (typeof launchCount === 'undefined') {
            GlobalUserActions.getLaunchCounts(uuid.v4(), { userId: userId});
        }
    }
    
    toggleCourseStats() {
        this.setState({ showCourseStats: !this.state.showCourseStats });
    }

    resendVerification() {
        const resendVerActionId = uuid.v4();
        this.setState({ resendVerActionId: resendVerActionId }, () => {
            GlobalUserActions.resendRegistrationVerification(resendVerActionId, { userId: this.props.user.id });
        });
    }

    updateAccountType(atToken) {
        const updateAccountTypeActionId = uuid.v4();
        this.setState({ updateAccountTypeActionId: updateAccountTypeActionId }, () => {
            GlobalUserActions.adminUpdateUser(updateAccountTypeActionId, { userId: this.props.user.id, account_type: atToken });
        });
    }

    render() {
        const _user = this.props.user;
        const _launchCount = this.state.userStoreState.launchCounts[_user.id];
        let orgRoleElems = [];
        if (
            this.props.channelRoles
            &&!this.props.channelRoles.pending
            && !this.props.channelRoles.error
        ) {
            const org_role_tokens = [
                Globals.users.roles.ORG_OWNER,
                Globals.users.roles.ORG_ADMIN,
                Globals.users.roles.ORG_SUPERVIEWER,
                Globals.users.roles.ORG_PROJECT_CREATOR,
                Globals.users.roles.ORG_MEMBER,
                Globals.users.roles.ORG_GUEST,
                Globals.users.roles.ORG_VIEWER,
            ];
            orgRoleElems.push(
                <li key={'heading'} className="heading-row" >
                    <label>Organization</label>
                    <span>Role</span>
                </li>
            );

            const org_roles = filter(this.props.channelRoles.channel_roles, role => includes(org_role_tokens, role.role_token));
            org_roles.forEach((role) => {
                orgRoleElems.push(
                    <li key={role.channel_id}>
                        <label>{role.channel_name}</label>
                        <span>{role.role_display_name}</span>
                    </li>
                );
            });
            if (org_roles.length === 0) {
                orgRoleElems.push(
                    <li key={0}>Not in any organizations</li>
                );
            }
        }

        return (
            <Card body bg="light" bsPrefix="card user-info">
                <h1 className="margin-bottom-lg">{_user.display_name}</h1>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">User Information</h3>
                    <hr className="thick hr-color-5"/>
                    <ul className="margin-bottom-lg labeled-list">
                        <li>
                            <label>ID</label>
                            <span>{_user.id}</span>
                        </li>
                        {has(_user, 'third_party_id') &&
                            <li>
                                <label>ID (3p)</label>
                                <span>{_user.third_party_id}</span>
                            </li>
                        }
                        <li>
                            <label>Hash</label>
                            <span>{_user.hash}</span>
                        </li>
                        {(!!_user.partner_id) &&
                            <li>
                                <label>Partner ID</label>
                                <span>{_user.partner_id}</span>
                            </li>
                        }
                        {(!!_user.email) &&
                            <li>
                                <label>Email</label>
                                <span>{_user.email}</span>
                            </li>
                        }
                        {(!!_user.third_party_email) &&
                            <li>
                                <label>Email (3p)</label>
                                <span>{_user.third_party_email}</span>
                            </li>
                        }
                        <li>
                            <label>Created at</label>
                            <span>
                                {!!_user.created_at ?
                                    <DateString date={_user.created_at} shortMonth showTime showSeconds />
                                    :
                                    'Unknown'
                                }
                            </span>
                        </li>
                        <li>
                            <label>Last active at</label>
                            <span>
                                {!!_user.active_at ?
                                    <DateString date={_user.active_at} shortMonth showTime showSeconds />
                                    :
                                    'Unknown'
                                }
                            </span>
                        </li>
                        <li>
                            <label>Registration Token</label>
                            <span>{_user.registration_token}</span>
                        </li>
                        <li>
                            <AdminUserLocalRoleEditor user={_user} />
                        </li>
                    </ul>
                    <ul className="labeled-list">
                        {has(_user, 'project_count') &&
                            <li>
                                <label>Owned Projects</label>
                                <span>{_user.project_count}</span>
                            </li>
                        }
                        {has(_user, 'activity_count') &&
                            <li>
                                <label>Owned Activities</label>
                                <span>{_user.activity_count}</span>
                            </li>
                        }
                        {(typeof _launchCount === 'number') &&
                            <li>
                                <label>Owned Activity Launches</label>
                                <span>{_launchCount}</span>
                            </li>
                        }
                        {has(_user, 'shared_project_count') &&
                            <li>
                                <label>Shared Projects</label>
                                <span>{_user.shared_project_count}</span>
                            </li>
                        }
                        {has(_user, 'shared_activity_count') &&
                            <li>
                                <label>Shared Activities</label>
                                <span>{_user.shared_activity_count}</span>
                            </li>
                        }
                    </ul>
                </div>
                
                {!!this.props.channelRoles &&
                    <div className="margin-bottom-lg">
                        {this.props.channelRoles.pending && <span>Loading organization roles...</span>}
                        {this.props.channelRoles.error && <span>Error loading organization roles.</span>}
                        <ul className="labeled-list">
                            {orgRoleElems} 
                        </ul>
                    </div>
                }

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Registration Information</h3>
                    <hr className="thick hr-color-5"/>
                    <ul className="labeled-list">
                        <li>
                            <label>Registered</label>
                            <span>{StringUtil.formatBool(this.props.user.registered)}</span>
                        </li>
                        <li>
                            <label>Registered via IDP</label>
                            <span>{StringUtil.formatBool(this.props.user.registered_idp)}</span>
                        </li>
                    </ul>
                    {(
                        this.props.user.type === Globals.users.type.LOCAL
                        && !this.props.user.registered
                        && !this.props.user.registered_idp
                        && Globals.users.is_user_account_type(this.props.user.account_type)
                    ) &&
                        <div>
                            <ErrorBox allowStackTrace error={this.state.verificationError} />
                            <div className="margin-top-md font-size-md">
                                Attempt to re-send registration verification email. (Will error for users with pending IDP verifications)
                            </div>
                            <Button
                                className="margin-top-md"
                                variant="warning"
                                disabled={!!this.state.resendVerActionId}
                                onClick={this.resendVerification}
                            >
                                Resend Registration Verification
                            </Button>
                            {this.state.verificationSuccess &&
                                <span className="margin-left-md">
                                    <AccessibleIcon className="fa fa-check fa-lg" label="Re-sending success" />
                                </span>
                            }
                        </div>
                    }
                </div>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Ban Information</h3>
                    <hr className="thick hr-color-5"/>
                    <AdminUserBanEditor user={this.props.user} />
                </div>

                {(GlobalConfig.DebugMode) &&
                    <div className="margin-bottom-lg">
                        <h3 className="heading-lg">Delete user</h3>
                        <hr className="thick hr-color-5"/>
                        <Button variant="danger" onClick={this.props.onDeleteUser}>Delete User</Button>
                    </div>
                }
                
                <hr className="thick hr-color-5"/>
                <div className="margin-bottom-lg">
                    <button title="Toggle Course Stats" className="btn btn-primary" onClick={this.toggleCourseStats}>{this.state.showCourseStats ? 'Hide Course Stats' : 'Course Stats'}</button>
                    {(this.state.showCourseStats) &&
                        <CourseStatsView channelId={_user.id} orgName={_user.display_name} />
                    }
                    <hr className="thick hr-color-5"/> 
                </div>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Partner Information</h3>
                    <hr className="thick hr-color-5"/>
                    <AdminUserPartnerKeyEditor user={this.props.user} />
                    { (!this.props.user.third_party_id) && <AdminLTISettingsView user={this.props.user} /> }
                </div>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Plan</h3>
                    <hr className="thick hr-color-5"/>
                    <ErrorBox allowStackTrace error={this.state.updateAccountTypeError} />
                    <AdminUserPlanSelector user={this.props.user} onUpdateAccountType={this.updateAccountType} />
                </div>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Template Permissions</h3>
                    <hr className="thick hr-color-5"/>
                    <AdminUserTemplatePermissionsEditor user={this.props.user} />
                </div>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Emulate User</h3>
                    <hr className="thick hr-color-5"/>
                    <AdminEmulateThirdPartyUser user={this.props.user} />
                </div>

                <div className="margin-bottom-lg">
                    <h3 className="heading-lg">Assignment Info</h3>
                    <hr className="thick hr-color-5"/>
                    <AdminUserAssignmentInfo user={this.props.user} />
                </div>
            </Card>
        );
    }
}

AdminUserInfoBlock.propTypes = propTypes;
AdminUserInfoBlock.defaultProps = defaultProps;

export default AdminUserInfoBlock;